// storage.js — 自作台の保存先
//
// 台の定義そのものは数 KB の JSON だが、液晶に流す映像は数十 MB になる。
// localStorage には入らないので IndexedDB に置く。
//
// プライベートモードや容量制限で IndexedDB が開けないことがある。そのときは
// メモリ上の Map に退避して、その場では編集も試し打ちもできる状態を保つ
// （リロードで消える旨はエディタ側が伝える）。

const DB_NAME = 'pachinkorokoro';
const DB_VER = 1;
const MACHINES = 'machines';
const VIDEOS = 'videos';

let dbPromise = null;
let usable = true;

// IndexedDB が使えないときの受け皿
const memory = { machines: new Map(), videos: new Map() };

/** IndexedDB に本当に保存できているか。false ならリロードで消える */
export const isPersistent = () => usable;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('no indexedDB')); return; }
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(MACHINES)) db.createObjectStore(MACHINES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(VIDEOS)) db.createObjectStore(VIDEOS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('blocked'));
  }).catch((e) => {
    usable = false;
    throw e;
  });
  return dbPromise;
}

function tx(store, mode, run) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = run(t.objectStore(store));
    t.oncomplete = () => resolve(req ? req.result : undefined);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

// ---------------------------------------------------------------- 台の定義

/** 保存されている自作台をすべて読む（並び順は slot 昇順） */
export async function listMachines() {
  try {
    const all = await tx(MACHINES, 'readonly', (s) => s.getAll());
    return (all || []).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  } catch {
    return [...memory.machines.values()].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  }
}

export async function saveMachine(m) {
  memory.machines.set(m.id, m);
  try {
    await tx(MACHINES, 'readwrite', (s) => s.put(m));
    return true;
  } catch {
    return false;
  }
}

export async function deleteMachine(id) {
  memory.machines.delete(id);
  for (const key of [...memory.videos.keys()]) {
    if (key.startsWith(id + ':')) memory.videos.delete(key);
  }
  try {
    await tx(MACHINES, 'readwrite', (s) => s.delete(id));
    // 映像はキーの前半に台の id を持たせてあるので、まとめて消せる
    await tx(VIDEOS, 'readwrite', (s) => {
      const req = s.getAllKeys();
      req.onsuccess = () => {
        for (const k of req.result) if (String(k).startsWith(id + ':')) s.delete(k);
      };
    });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- 映像

export const videoKey = (machineId, state) => `${machineId}:${state}`;

export async function putVideo(key, blob) {
  memory.videos.set(key, blob);
  try {
    await tx(VIDEOS, 'readwrite', (s) => s.put(blob, key));
    return true;
  } catch {
    return false;
  }
}

export async function getVideo(key) {
  try {
    const v = await tx(VIDEOS, 'readonly', (s) => s.get(key));
    if (v) return v;
  } catch { /* メモリ側で探す */ }
  return memory.videos.get(key) || null;
}

export async function deleteVideo(key) {
  memory.videos.delete(key);
  try {
    await tx(VIDEOS, 'readwrite', (s) => s.delete(key));
  } catch { /* 消せなくても実害はない */ }
}

/** 保存済みの映像がどれくらい容量を使っているか（表示用・取れなければ null） */
export async function usage() {
  try {
    const e = await navigator.storage?.estimate?.();
    return e ? { used: e.usage || 0, quota: e.quota || 0 } : null;
  } catch {
    return null;
  }
}
